/**
 * Wave-Viz 1：销售视角「今日待办聚合卡」（V-FE-dashboard）
 *
 * 调 GET /dashboard/today-summary，返回 { pendingFollowup, pendingInvoice, pendingPayment, pendingApproval } 四个 number。
 * 端点按 actor 本人 scope 计数（sales 视角，owner/creator/submittedBy=自己），@RequirePermissions('customer:view')。
 * 四个数字格子，各格点击 nav 到对应列表：
 *   待跟进 → /customers?stale=7（与 dashboard.alerts staleFollowUp 同口径，下钻数对得上）
 *   待开票 → /invoices?tab=pending
 *   待收款 → /payments?tab=pending
 *   待审批 → /approvals（销售视角=我提交的、审批中的单）
 *
 * 仅 role==='sales' 显示（仿 MyIncentiveCard，规避 sales 持 report:view_team 致 viewAsPersonal=false 的不确定性）。
 * 卡片样式仿 Dashboard.tsx AlertsRow 四格 + Link 跳转 + value>0 高亮。
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { $ } from '../../lib/api';
import { HomeCard } from '../ui';

type TodaySummary = {
  pendingFollowup: number;
  pendingInvoice: number;
  pendingPayment: number;
  pendingApproval: number | null; // 后端对 sales/academic 按权限降级返 null → 前端隐藏待审批格
};

export function TodaySummaryCard() {
  const { data } = useQuery<TodaySummary>({
    queryKey: ['dashboard/today-summary'],
    queryFn: () => $.get('/dashboard/today-summary'),
    refetchInterval: 60_000,
  });

  // 待跟进 红 / 待开票 蓝 / 待收款 橙 / 待审批 紫，前三格点击跳对应列表 tab。
  // ★待审批：后端按权限降级——sales/academic 返 pendingApproval=null → 不渲染该格（彻底不显，避免无意义空块）；
  //   有 approval:view 的角色才显，且 link=null 纯展示（销售本就跳不进 /approvals，此处统一不跳转）。★
  const items = [
    { label: '待跟进', value: data?.pendingFollowup ?? 0, color: 'var(--danger)', emoji: '🔴', link: '/customers?stale=7' as string | null, tip: '' },
    { label: '待开票', value: data?.pendingInvoice ?? 0, color: '#1677ff', emoji: '📄', link: '/invoices?tab=pending' as string | null, tip: '' },
    { label: '待收款', value: data?.pendingPayment ?? 0, color: '#fa8c16', emoji: '💰', link: '/payments?tab=pending' as string | null, tip: '' },
    ...(data?.pendingApproval != null
      ? [{ label: '待审批', value: data.pendingApproval, color: 'var(--purple)', emoji: '📋', link: null as string | null, tip: '你提交的单据正在主管审批中' }]
      : []),
  ];

  return (
    <HomeCard title={<>今日待办 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>待处理事项 · 点击查看</span></>}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, padding: '4px 14px 14px' }}>
        {items.map((it) => {
          const box = (
            <div title={it.tip || undefined} style={{
              background: 'var(--bg-1)',
              border: `1px solid ${it.value > 0 ? it.color : 'var(--border)'}`,
              borderRadius: 8, padding: '16px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              cursor: it.link ? 'pointer' : 'default',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
                <span style={{ marginRight: 4 }}>{it.emoji}</span>
                {it.label}
              </div>
              <div style={{
                fontSize: 26, fontWeight: 600,
                color: it.value > 0 ? it.color : 'var(--text-2)',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {it.value}
              </div>
            </div>
          );
          return it.link
            ? <Link key={it.label} to={it.link} style={{ textDecoration: 'none' }}>{box}</Link>
            : <div key={it.label}>{box}</div>;
        })}
      </div>
    </HomeCard>
  );
}
